/**
 * Pine. See treeOak.ts for the shape of this file and the LOD ladder.
 *
 * The lod-1/2 stand-ins are stacks of tapering cylinders rather than a cone, and that is faithful
 * rather than a compromise: the LOD-0 canopy is ALREADY 3-4 stacked tapering layers of decreasing
 * radius. A coarse STACK of the same silhouette turned out to read as steps, though, so the coarse
 * canopy is a single smooth cone instead (see lodParts).
 */
import { mat } from '../../../materials/index.js';
import { groundSink, pinPart } from '../coarseModel.js';
import { conePart, cylinderPart, } from '../../../voxel/buildParts.js';
import { MAX_PACKABLE_LOD } from '../../../util/packedKey.js';
import { StampType } from '../StampTypes.js';
import { cylinder, sphere } from '../../../voxel/blobs.js';
/** Fixed trunk height in voxels (4m) - canopy starts here */
const TREE_TRUNK_HEIGHT = 16;
function params(variant) {
    const canopyLayers = 3 + (variant % 2);
    const totalCanopyHeight = 20 + (variant % 3) * 4; // 20-28 voxels of canopy
    const layerHeight = totalCanopyHeight / canopyLayers;
    // 50% bigger: 6-7.5 base radius instead of 4-5
    const baseR = 6 + (variant % 3) * 0.75;
    return {
        trunkH: TREE_TRUNK_HEIGHT,
        // A third of the way up the canopy — enough to read through the leaves at every level.
        trunkTop: TREE_TRUNK_HEIGHT + totalCanopyHeight * 0.35,
        trunkR: 1.2 + (variant % 2) * 0.2, // 1.2-1.4 - consistent with oak
        canopyLayers,
        totalCanopyHeight,
        layerHeight,
        canopyBaseR: 6,
        radiusBonus: (variant % 3) * 0.75,
        layerStep: 1.2,
        baseR,
        // Last layer's layerTop: canopyStart + (layers-1)*layerHeight + layerHeight*1.3
        canopyTop: TREE_TRUNK_HEIGHT + layerHeight * (canopyLayers + 0.3),
        tipR: 1,
        crownBase: TREE_TRUNK_HEIGHT - baseR, // the lowest layer sphere bulges down by its radius
        barkMat: variant % 2 === 0 ? mat('bark2') : mat('bark3'),
        leafMat: mat('leaves_trans_3'),
    };
}
/** LOD 0 — the original generatePineTree body, reading its numbers from params. */
function lod0(p, variant) {
    const voxels = [];
    // Canopy first, trunk last — see treeOak.lod0 for why the order is load-bearing.
    const canopyStart = p.trunkH; // Start at trunk top, same as oak
    for (let layer = 0; layer < p.canopyLayers; layer++) {
        const layerY = canopyStart + layer * p.layerHeight;
        const layerRadius = (p.canopyBaseR - layer * p.layerStep + p.radiusBonus);
        const layerTop = layerY + p.layerHeight * 1.3;
        // Create tapered canopy layer
        for (let y = layerY; y < layerTop; y++) {
            const progress = (y - layerY) / (layerTop - layerY);
            const radius = layerRadius * (1 - progress * 0.7);
            voxels.push(...sphere(0, Math.floor(y), 0, radius, p.leafMat, 0.5, variant + layer));
        }
    }
    // Trunk last, so it reads through the leaves it penetrates.
    voxels.push(...cylinder(0, 0, p.trunkR, 0, p.trunkTop, p.barkMat));
    return voxels;
}
/** See treeOak's LOD1_TRUNK_R — the real 1.2 radius is sub-voxel at every coarse stride. */
const LOD1_TRUNK_R = 2.5;
/** No ceiling — see treeOak. */
const MAX_LOD = MAX_PACKABLE_LOD;
function lodParts(p, lod) {
    const stride = 1 << lod;
    if (lod >= 1 && lod <= 3) {
        // ONE CONE, not a stack of cylinders. The LOD-0 canopy is a stack of tapering layers, so a stack
        // seemed faithful — but a stack can only change radius in jumps, which drew as flat plateaus with
        // abrupt shoulders (25 -> 21 -> 9 voxels per row at lod 1), and it could not be refined because
        // every step needs an overlap with the next. A cone tapers continuously and has no junctions.
        //
        const width = lod === 3 ? 1.5 : lod === 2 ? 1.4 : 1.15;
        const trunk = lod === 1
            ? cylinderPart(LOD1_TRUNK_R, p.trunkTop + groundSink(stride), p.barkMat, -groundSink(stride))
            : pinPart(p.trunkTop, p.barkMat, stride);
        //
        // The crown's own base (see params.crownBase) — well below trunkH, matching where LOD 0's foliage
        // actually reaches. No sink fudge is needed on top of it: the row at trunkH is now deep inside the
        // cone rather than sitting on its base face. Overlap with the trunk comes for free, since the
        // trunk runs past here up to trunkTop.
        const base = p.crownBase;
        const baseR = Math.max(p.baseR * width, 1.05 * stride);
        // THE TIP IS BOUNDED BY THE LATTICE, NOT ONLY BY THE TREE. Anything under one coarse voxel of
        // radius renders as the bare axis, so a tip of p.tipR (1 fine voxel) leaves a RUN of one-voxel
        // rows: measured at lod 2, radius fell under a voxel at fine 27.5 and rows 28/32/36 all drew as a
        // 3 m tall, 1 m wide needle. Faithful — LOD 0 is sub-voxel there too — but a needle is worse than
        // not drawing that detail. Held just under a coarse voxel, only the final row goes axis-only: a
        // one-row point instead of a spike.
        const tip = Math.max(p.tipR, 0.8 * stride);
        return [
            trunk,
            // No taper at lod 3: the crown is under three coarse rows there, so a taper has nothing to
            // resolve and only costs body — the cone collapsed to 1-voxel rows. A drum keeps the mass.
            conePart(baseR, lod === 3 ? baseR : tip, base, p.canopyTop, p.leafMat),
        ];
    }
    return [pinPart(p.canopyTop, p.leafMat, stride)];
}
export const TREE_PINE_SPEC = {
    type: StampType.TREE_PINE,
    maxLod: MAX_LOD,
    params,
    lod0,
    lodParts,
};
//# sourceMappingURL=treePine.js.map